import type { Feature } from 'geojson';
import { Coordinates, Level } from '@wemap/geo';
import { Itinerary } from '@wemap/routers';
import type { Map } from 'maplibre-gl';
import { getLevelProperty } from '@wemap/map';
import type { WemapMap } from '@wemap/map';

import { MULTIROUTERS_ENDPOINT, MULTIROUTERS_ENDPOINT_DEV } from './config.js';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource.js';

export default class ItineraryHandler {

    private readonly map: Map;

    _itinerary: Itinerary | undefined;

    /** @type {OsmWay[]} */
    _highways: any;

    _routingBoundsFeature: any;

    _routingIoFeatures = [];

    _disabledWays = [];

    _graphGeojson: any;

    emmid: number;
    allPinpoints: any[] = [];

    sourceId = 'itinerary';
    projectionsSourceId = 'itinerary-projections';
    environment: 'prod' | 'dev';

    layersID = ['itinerary-layer', 'itinerary-projections-layer'];

    constructor(wemapMap: WemapMap, emmid: number, options: { environment?: 'prod' | 'dev' } = {}) {
        const { environment = 'prod' } = options;
        this.environment = environment;
        this.map = wemapMap.maplibre;
        this.emmid = emmid;

        wemapMap.addSource(this.sourceId, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: 'itinerary-layer',
            type: 'line',
            source: this.sourceId,
            paint: {
                'line-color': '#45b4dd',
                'line-width': 6
            }
        }, { indoor: true });

        wemapMap.addSource(this.projectionsSourceId, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: 'itinerary-projections-layer',
            type: 'line',
            source: this.projectionsSourceId,
            paint: {
                'line-color': '#2980b9',
                'line-width': 3,
                'line-dasharray': [2, 2]
            }
        }, { indoor: true });
    }

    get itinerary(): Itinerary | undefined {
        return this._itinerary;
    }


    set itinerary(itinerary: Itinerary | undefined) {
        this._itinerary = itinerary;
        this._updateFeatures();
    }

    getMultiRoutersEndpoint() {
        return this.environment === 'prod' ? MULTIROUTERS_ENDPOINT : MULTIROUTERS_ENDPOINT_DEV;
    }

    checkMultipleItineraries = async (lngLat: { lat: number, lng: number, level: number | null }): Promise<null | number[]> => {
        return await fetch(`${this.getMultiRoutersEndpoint()}/directions-multiple`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                mapId: this.emmid,
                origin: {
                    lat: lngLat.lat,
                    lng: lngLat.lng,
                    level: lngLat.level
                },
                destinations: this.allPinpoints.map((pinpoint) => pinpoint.id),
                travelMode: 'WALK',
                output: {
                    "distanceAndDurationOnly": true
                }  
            })
        }).then((res) => res.json()).then((res) => {
            const wrongItineraries = res.itineraries.filter((itinerary: any) => itinerary.status.code !== 0);

            if (wrongItineraries.length) {
                return wrongItineraries.map((itinerary: any) => this.allPinpoints[itinerary.destinationIndex].id);
            }

            return null;
        });
    }

    _createProjectionFeature = (p1: Coordinates, p2: Coordinates): Feature => {
        return {
            type: 'Feature',
            properties: {
                ...getLevelProperty(Level.union(p1.level, p2.level))
            },
            geometry: {
                type: 'LineString',
                coordinates: [
                    [p1.lng, p1.lat],
                    [p2.lng, p2.lat]
                ]
            }
        };
    }

    _updateFeatures = () => {
        if (!this.map.style) {
            return;
        }

        if (this.itinerary) {
            const segments: [Coordinates, Coordinates][] = [];

            for (let index = 0; index < this.itinerary.coords.length - 1; index++) {
                segments.push([this.itinerary.coords[index], this.itinerary.coords[index + 1]]);
            }

            const features: Feature[] = segments.map((segment) => ({
                type: 'Feature',
                properties: {
                    ...getLevelProperty(Level.union(segment[0].level, segment[1].level))
                },
                geometry: {
                    type: 'LineString',
                    coordinates: segment.map((coord) => [coord.lng, coord.lat])
                }
            }));

            setGeoJsonSourceData(this.map, this.sourceId, {
                type: 'FeatureCollection',
                features
            });

            const originProjectionFeature = this._createProjectionFeature(
                this.itinerary.origin,
                this.itinerary.coords[0]
            );

            const destinationProjectionFeature = this._createProjectionFeature(
                this.itinerary.coords[this.itinerary.coords.length - 1],
                this.itinerary.destination
            );

            setGeoJsonSourceData(this.map, this.projectionsSourceId, {
                type: 'FeatureCollection',
                features: [originProjectionFeature, destinationProjectionFeature]
            });
        } else {
            setGeoJsonSourceData(this.map, this.sourceId, EMPTY_FEATURE_COLLECTION);
            setGeoJsonSourceData(this.map, this.projectionsSourceId, EMPTY_FEATURE_COLLECTION);
        }
    };

    hideItinerary = () => {
        setGeoJsonSourceData(this.map, this.sourceId, EMPTY_FEATURE_COLLECTION);
        setGeoJsonSourceData(this.map, this.projectionsSourceId, EMPTY_FEATURE_COLLECTION);
    }

    showItinerary = () => {
        this._updateFeatures();
    }
}
